import type React from "react";
import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useState,
} from "react";

import type {
  defaultSnapProps,
  snapPoints,
} from "react-spring-bottom-sheet/dist/types";
import { useDebounceValue } from "usehooks-ts";

import { selectUserSettingsPanelOpen } from "@/features/app/appSlice";

import { useAppSelector } from "@/app/hooks";
import { clamp } from "@/libs/geo";
import {
  useBreakpoints,
  useThrottle,
  useWindowDimensions,
} from "../../app/hooks";
import type { BottomDrawerRef } from "./BottomDrawer";
import useBottomDrawerDimensions from "./hooks/useBottomDrawerDimensions";
import useBottomDrawerRefs from "./hooks/useBottomDrawerRefs";

export enum BottomDrawerState {
  open,
  closed,
  minimized,
}

export enum BottomDrawerPosition {
  expanded,
  halfWay,
  minimized,
}

export type BottomDrawerContextType = {
  state: BottomDrawerState;
  hide: () => void;
  minimize: () => void;
  maximize: () => void;
  show: () => void;
  header: ReactNode | null;
  setHeader: (header: ReactNode | null) => void;
  content: ReactNode | null;
  setContent: (content: ReactNode | null) => void;
  footer: ReactNode | null;
  setFooter: (footer: ReactNode | null) => void;
  defaultSnapPoint: number | ((props: defaultSnapProps) => number) | null;
  setDefaultSnapPoint: (
    s: (() => BottomDrawerContextType["defaultSnapPoint"]) | null,
  ) => void;
  snapPoints: snapPoints | null;
  setSnapPoints: (s: (() => snapPoints) | null) => void;
  isBottomDrawerReady: boolean;
  setIsBottomDrawerReady: (x: boolean) => void;
  bottomDrawerRef?: BottomDrawerRef;
  setBottomDrawerRef: (ref: BottomDrawerRef) => void;
  contentHeight: number | undefined;
  overlayHeight: number | undefined;
  headerHeight: number | undefined;
  footerHeight: number | undefined;
  bottomDrawerHeight: number;
  setBottomDrawerHeight: (x: number) => void;
  dragProgress: number;
  topOffset: number;
  setTopOffset: (x: number) => void;
  position: BottomDrawerPosition;
  expandOnDrag: boolean;
  setExpandOnDrag: (x: boolean) => void;
  contentRef: RefObject<HTMLElement> | null;
  overlayRef: RefObject<HTMLElement> | null;
  isLocked: boolean;
  setIsLocked: (x: boolean) => void;
  hideSiblings: boolean;
  setHideSiblings: (x: boolean) => void;
  showBackDrop: boolean;
  setShowBackDrop: (x: boolean) => void;
};

export const BottomDrawerContext =
  createContext<BottomDrawerContextType | null>(null);

export const useBottomDrawer = () => {
  const value = useContext(BottomDrawerContext);
  if (!value) {
    throw new Error(
      "useBottomDrawer must be used within a <BottomDrawerProvider/>",
    );
  }
  return value;
};

export const BottomDrawerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // State
  const [drawerState, setDrawerState] = useState<
    BottomDrawerContextType["state"]
  >(BottomDrawerState.closed);
  const [bottomDrawerRef, setBottomDrawerRef] =
    useState<BottomDrawerContextType["bottomDrawerRef"]>();
  const [header, setHeader] = useState<BottomDrawerContextType["header"]>();
  const [content, setContent] = useState<BottomDrawerContextType["content"]>();
  const [footer, setFooter] = useState<BottomDrawerContextType["footer"]>();
  const [snapPoints, setSnapPoints] =
    useState<BottomDrawerContextType["snapPoints"]>(null);
  const [defaultSnap, setDefaultSnap] =
    useState<BottomDrawerContextType["defaultSnapPoint"]>(null);
  const [bottomDrawerHeight, setBottomDrawerHeight] = useState(0);
  const [dragProgress, setDragProgress] = useState(1);
  const setDragProgressThrottled = useThrottle(setDragProgress, 50);
  const [topOffset, setTopOffset] = useState(0);
  const [position, setPosition] = useDebounceValue(
    BottomDrawerPosition.minimized,
    100,
  );
  const [expandOnDrag, setExpandOnDrag] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [hideSiblings, setHideSiblings] = useState(false);
  const [showBackDrop, setShowBackDrop] = useState(false);
  const [isBottomDrawerReady, setIsBottomDrawerReady] = useState(false);

  // Redux
  const userSettingsPanelOpen = useAppSelector(selectUserSettingsPanelOpen);

  // Custom hooks
  const { isMobileVersion } = useBreakpoints();
  const { contentRef, overlayRef, elements } = useBottomDrawerRefs();

  const { contentHeight, overlayHeight, footerHeight, headerHeight } =
    useBottomDrawerDimensions({
      contentRef: elements.content,
      footerRef: elements.footer,
      headerRef: elements.header,
      overlayRef: elements.overlay,
    });
  const { height } = useWindowDimensions();

  // Callbacks
  const show = () => {
    // TODO this check for mobileVersion should probably not be here
    if (isMobileVersion) setDrawerState(BottomDrawerState.open);
  };

  const maximize = () => {
    setPosition(BottomDrawerPosition.expanded);
  };

  const minimize = () => {
    setDrawerState(BottomDrawerState.minimized);
  };

  const hide = () => {
    setDrawerState(BottomDrawerState.closed);
  };

  // Use effects
  useEffect(() => {
    if (!isMobileVersion) {
      hide();
    } else {
      if (!content && !header && !footer) {
        minimize();
      } else {
        show();
      }
    }
  }, [isMobileVersion, content, header, footer]);

  useEffect(() => {
    if (height === 0) return;

    const progress = clamp((overlayHeight || 0) / (height - topOffset), 0, 1);
    setDragProgressThrottled(progress);
  }, [overlayHeight, topOffset, height]);

  useEffect(() => {
    const roundedProgress = Math.round(dragProgress * 100);
    if (roundedProgress === 0) {
      setPosition(BottomDrawerPosition.minimized);
    } else if (roundedProgress === 100) {
      setPosition(BottomDrawerPosition.expanded);
    } else {
      setPosition(BottomDrawerPosition.halfWay);
    }
  }, [dragProgress]);

  // Hide bottom drawer from accessibility DOM when user settings panel is open
  useEffect(() => {
    overlayRef?.current?.setAttribute(
      "aria-hidden",
      `${userSettingsPanelOpen}`,
    );
  }, [userSettingsPanelOpen]);

  return (
    <BottomDrawerContext.Provider
      value={{
        bottomDrawerHeight,
        bottomDrawerRef,
        content,
        contentHeight,
        contentRef,
        defaultSnapPoint: defaultSnap,
        dragProgress,
        expandOnDrag,
        footer,
        footerHeight,
        header,
        headerHeight,
        hide,
        hideSiblings,
        isBottomDrawerReady,
        isLocked,
        maximize,
        minimize,
        overlayHeight,
        overlayRef,
        position,
        setBottomDrawerHeight,
        setBottomDrawerRef,
        setContent,
        setDefaultSnapPoint: setDefaultSnap,
        setExpandOnDrag,
        setFooter,
        setHeader,
        setHideSiblings,
        setIsBottomDrawerReady,
        setIsLocked,
        setShowBackDrop,
        setSnapPoints,
        setTopOffset,
        show,
        showBackDrop,
        snapPoints,
        state: drawerState,
        topOffset,
      }}
    >
      {children}
    </BottomDrawerContext.Provider>
  );
};
